import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { ValidationBody } from 'ts/interfaces/Validation';
import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { MainContext } from 'contexts/MainContext';

interface PhoneInputProps {
  onChange: (value: string) => void;
  value: string;
  isSubmitted: boolean;
  validation: ValidationBody;
  className?: string;
  initialValue?: string;
}

const PhoneInput: FC<PhoneInputProps> = (props) => {
  const [codes, setCodes] = useState<Select[]>([]);
  const [code, setCode] = useState('');
  const [val, setVal] = useState('');
  const { t } = useTranslation();
  const { initSiteData } = useContext(MainContext);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (props.initialValue) {
      setVal(props.initialValue);
      onChange({ c: code, v: props.initialValue });
    }
    setIsInitialized(true);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isInitialized && !props.value) {
      setVal('');
      onChange({ c: code, v: '' });
    } else if (isInitialized && props.value) {
      const phoneValue = props.value || '';

      const findMatchingCode = (phone: string) => {
        let matchedCode = listRef.current.find((c: any) => phone.startsWith(c.code));

        if (!matchedCode) {
          matchedCode = listRef.current.find((c: any) => phone.startsWith(c.code.substring(1)));
        }

        return matchedCode;
      };

      const matchedCode = findMatchingCode(phoneValue);

      if (matchedCode) {
        setCode(matchedCode.code);

        const codeToRemove = phoneValue.startsWith(matchedCode.code)
          ? matchedCode.code
          : matchedCode.code.substring(1);

        setVal(phoneValue.replace(codeToRemove, ''));
      }
    }
  }, [props.value, isInitialized]); // eslint-disable-line

  useEffect(() => {
    if (initSiteData?.other?.defaultPhoneCode) {
      setCode(initSiteData.other.defaultPhoneCode);
    } else {
      setCode('+972');
    }
  }, [initSiteData]);
  const listRef = useRef([
    {
      code: '+93',
      name: 'Afghanistan',
      short: 'AF',
    },
    {
      code: '+355',
      name: 'Albania',
      short: 'AL',
    },
    {
      code: '+213',
      name: 'Algeria',
      short: 'DZ',
    },
    {
      code: '+1684',
      name: 'American Samoa',
      short: 'AS',
    },
    {
      code: '+376',
      name: 'Andorra',
      short: 'AD',
    },
    {
      code: '+244',
      name: 'Angola',
      short: 'AO',
    },
    {
      code: '+1264',
      name: 'Anguilla',
      short: 'AI',
    },
    {
      code: '+672',
      name: 'Antarctica',
      short: 'AQ',
    },
    {
      code: '+1268',
      name: 'Antigua and Barbuda',
      short: 'AG',
    },
    {
      code: '+54',
      name: 'Argentina',
      short: 'AR',
    },
    {
      code: '+374',
      name: 'Armenia',
      short: 'AM',
    },
    {
      code: '+297',
      name: 'Aruba',
      short: 'AW',
    },
    {
      code: '+61',
      name: 'Australia',
      short: 'AU',
    },
    {
      code: '+43',
      name: 'Austria',
      short: 'AT',
    },
    {
      code: '+994',
      name: 'Azerbaijan',
      short: 'AZ',
    },
    {
      code: '+1242',
      name: 'Bahamas',
      short: 'BS',
    },
    {
      code: '+973',
      name: 'Bahrain',
      short: 'BH',
    },
    {
      code: '+880',
      name: 'Bangladesh',
      short: 'BD',
    },
    {
      code: '+1246',
      name: 'Barbados',
      short: 'BB',
    },
    {
      code: '+375',
      name: 'Belarus',
      short: 'BY',
    },
    {
      code: '+32',
      name: 'Belgium',
      short: 'BE',
    },
    {
      code: '+501',
      name: 'Belize',
      short: 'BZ',
    },
    {
      code: '+229',
      name: 'Benin',
      short: 'BJ',
    },
    {
      code: '+1441',
      name: 'Bermuda',
      short: 'BM',
    },
    {
      code: '+975',
      name: 'Bhutan',
      short: 'BT',
    },
    {
      code: '+591',
      name: 'Bolivia',
      short: 'BO',
    },
    {
      code: '+387',
      name: 'Bosnia and Herzegovina',
      short: 'BA',
    },
    {
      code: '+267',
      name: 'Botswana',
      short: 'BW',
    },
    {
      code: '+55',
      name: 'Brazil',
      short: 'BR',
    },
    {
      code: '+246',
      name: 'British Indian Ocean Territory',
      short: 'IO',
    },
    {
      code: '+1284',
      name: 'British Virgin Islands',
      short: 'VG',
    },
    {
      code: '+673',
      name: 'Brunei',
      short: 'BN',
    },
    {
      code: '+359',
      name: 'Bulgaria',
      short: 'BG',
    },
    {
      code: '+226',
      name: 'Burkina Faso',
      short: 'BF',
    },
    {
      code: '+257',
      name: 'Burundi',
      short: 'BI',
    },
    {
      code: '+855',
      name: 'Cambodia',
      short: 'KH',
    },
    {
      code: '+237',
      name: 'Cameroon',
      short: 'CM',
    },
    {
      code: '+1',
      name: 'Canada',
      short: 'CA',
    },
    {
      code: '+238',
      name: 'Cape Verde',
      short: 'CV',
    },
    {
      code: '+599',
      name: 'Caribbean Netherlands',
      short: 'BQ',
    },
    {
      code: '+1345',
      name: 'Cayman Islands',
      short: 'KY',
    },
    {
      code: '+236',
      name: 'Central African Republic',
      short: 'CF',
    },
    {
      code: '+235',
      name: 'Chad',
      short: 'TD',
    },
    {
      code: '+56',
      name: 'Chile',
      short: 'CL',
    },
    {
      code: '+86',
      name: 'China',
      short: 'CN',
    },
    {
      code: '+61',
      name: 'Christmas Island',
      short: 'CX',
    },
    {
      code: '+61',
      name: 'Cocos Keeling Islands',
      short: 'CC',
    },
    {
      code: '+57',
      name: 'Colombia',
      short: 'CO',
    },
    {
      code: '+269',
      name: 'Comoros',
      short: 'KM',
    },
    {
      code: '+243',
      name: 'Congo DR',
      short: 'CD',
    },
    {
      code: '+242',
      name: 'Congo Republic',
      short: 'CG',
    },
    {
      code: '+682',
      name: 'Cook Islands',
      short: 'CK',
    },
    {
      code: '+506',
      name: 'Costa Rica',
      short: 'CR',
    },
    {
      code: '+225',
      name: 'Cote dIvoire',
      short: 'CI',
    },
    {
      code: '+385',
      name: 'Croatia',
      short: 'HR',
    },
    {
      code: '+53',
      name: 'Cuba',
      short: 'CU',
    },
    {
      code: '+599',
      name: 'Curacao',
      short: 'CW',
    },
    {
      code: '+357',
      name: 'Cyprus',
      short: 'CY',
    },
    {
      code: '+420',
      name: 'Czech Republic',
      short: 'CZ',
    },
    {
      code: '+45',
      name: 'Denmark',
      short: 'DK',
    },
    {
      code: '+253',
      name: 'Djibouti',
      short: 'DJ',
    },
    {
      code: '+1767',
      name: 'Dominica',
      short: 'DM',
    },
    {
      code: '+1809',
      name: 'Dominican Republic',
      short: 'DO',
    },
    {
      code: '+670',
      name: 'East Timor',
      short: 'TL',
    },
    {
      code: '+593',
      name: 'Ecuador',
      short: 'EC',
    },
    {
      code: '+20',
      name: 'Egypt',
      short: 'EG',
    },
    {
      code: '+503',
      name: 'El Salvador',
      short: 'SV',
    },
    {
      code: '+240',
      name: 'Equatorial Guinea',
      short: 'GQ',
    },
    {
      code: '+291',
      name: 'Eritrea',
      short: 'ER',
    },
    {
      code: '+372',
      name: 'Estonia',
      short: 'EE',
    },
    {
      code: '+251',
      name: 'Ethiopia',
      short: 'ET',
    },
    {
      code: '+500',
      name: 'Falkland Islands',
      short: 'FK',
    },
    {
      code: '+298',
      name: 'Faroe Islands',
      short: 'FO',
    },
    {
      code: '+679',
      name: 'Fiji',
      short: 'FJ',
    },
    {
      code: '+358',
      name: 'Finland',
      short: 'FI',
    },
    {
      code: '+33',
      name: 'France',
      short: 'FR',
    },
    {
      code: '+594',
      name: 'French Guiana',
      short: 'GF',
    },
    {
      code: '+689',
      name: 'French Polynesia',
      short: 'PF',
    },
    {
      code: '+241',
      name: 'Gabon',
      short: 'GA',
    },
    {
      code: '+220',
      name: 'Gambia',
      short: 'GM',
    },
    {
      code: '+995',
      name: 'Georgia',
      short: 'GE',
    },
    {
      code: '+49',
      name: 'Germany',
      short: 'DE',
    },
    {
      code: '+233',
      name: 'Ghana',
      short: 'GH',
    },
    {
      code: '+350',
      name: 'Gibraltar',
      short: 'GI',
    },
    {
      code: '+30',
      name: 'Greece',
      short: 'GR',
    },
    {
      code: '+299',
      name: 'Greenland',
      short: 'GL',
    },
    {
      code: '+1473',
      name: 'Grenada',
      short: 'GD',
    },
    {
      code: '+590',
      name: 'Guadeloupe',
      short: 'GP',
    },
    {
      code: '+1671',
      name: 'Guam',
      short: 'GU',
    },
    {
      code: '+502',
      name: 'Guatemala',
      short: 'GT',
    },
    {
      code: '+441481',
      name: 'Guernsey',
      short: 'GG',
    },
    {
      code: '+224',
      name: 'Guinea',
      short: 'GN',
    },
    {
      code: '+245',
      name: 'Guinea Bissau',
      short: 'GW',
    },
    {
      code: '+592',
      name: 'Guyana',
      short: 'GY',
    },
    {
      code: '+509',
      name: 'Haiti',
      short: 'HT',
    },
    {
      code: '+504',
      name: 'Honduras',
      short: 'HN',
    },
    {
      code: '+852',
      name: 'Hong Kong',
      short: 'HK',
    },
    {
      code: '+36',
      name: 'Hungary',
      short: 'HU',
    },
    {
      code: '+354',
      name: 'Iceland',
      short: 'IS',
    },
    {
      code: '+91',
      name: 'India',
      short: 'IN',
    },
    {
      code: '+62',
      name: 'Indonesia',
      short: 'ID',
    },
    {
      code: '+98',
      name: 'Iran',
      short: 'IR',
    },
    {
      code: '+964',
      name: 'Iraq',
      short: 'IQ',
    },
    {
      code: '+353',
      name: 'Ireland',
      short: 'IE',
    },
    {
      code: '+441624',
      name: 'Isle of Man',
      short: 'IM',
    },
    {
      code: '+972',
      name: 'Israel',
      short: 'IL',
    },
    {
      code: '+39',
      name: 'Italy',
      short: 'IT',
    },
    {
      code: '+1876',
      name: 'Jamaica',
      short: 'JM',
    },
    {
      code: '+81',
      name: 'Japan',
      short: 'JP',
    },
    {
      code: '+441534',
      name: 'Jersey',
      short: 'JE',
    },
    {
      code: '+962',
      name: 'Jordan',
      short: 'JO',
    },
    {
      code: '+7',
      name: 'Kazakhstan',
      short: 'KZ',
    },
    {
      code: '+254',
      name: 'Kenya',
      short: 'KE',
    },
    {
      code: '+686',
      name: 'Kiribati',
      short: 'KI',
    },
    {
      code: '+383',
      name: 'Kosovo',
      short: 'XK',
    },
    {
      code: '+965',
      name: 'Kuwait',
      short: 'KW',
    },
    {
      code: '+996',
      name: 'Kyrgyzstan',
      short: 'KG',
    },
    {
      code: '+856',
      name: 'Laos',
      short: 'LA',
    },
    {
      code: '+371',
      name: 'Latvia',
      short: 'LV',
    },
    {
      code: '+961',
      name: 'Lebanon',
      short: 'LB',
    },
    {
      code: '+266',
      name: 'Lesotho',
      short: 'LS',
    },
    {
      code: '+231',
      name: 'Liberia',
      short: 'LR',
    },
    {
      code: '+218',
      name: 'Libya',
      short: 'LY',
    },
    {
      code: '+423',
      name: 'Liechtenstein',
      short: 'LI',
    },
    {
      code: '+370',
      name: 'Lithuania',
      short: 'LT',
    },
    {
      code: '+352',
      name: 'Luxembourg',
      short: 'LU',
    },
    {
      code: '+853',
      name: 'Macau',
      short: 'MO',
    },
    {
      code: '+389',
      name: 'North Macedonia',
      short: 'MK',
    },
    {
      code: '+261',
      name: 'Madagascar',
      short: 'MG',
    },
    {
      code: '+265',
      name: 'Malawi',
      short: 'MW',
    },
    {
      code: '+60',
      name: 'Malaysia',
      short: 'MY',
    },
    {
      code: '+960',
      name: 'Maldives',
      short: 'MV',
    },
    {
      code: '+223',
      name: 'Mali',
      short: 'ML',
    },
    {
      code: '+356',
      name: 'Malta',
      short: 'MT',
    },
    {
      code: '+692',
      name: 'Marshall Islands',
      short: 'MH',
    },
    {
      code: '+596',
      name: 'Martinique',
      short: 'MQ',
    },
    {
      code: '+222',
      name: 'Mauritania',
      short: 'MR',
    },
    {
      code: '+230',
      name: 'Mauritius',
      short: 'MU',
    },
    {
      code: '+262',
      name: 'Mayotte',
      short: 'YT',
    },
    {
      code: '+52',
      name: 'Mexico',
      short: 'MX',
    },
    {
      code: '+691',
      name: 'Micronesia',
      short: 'FM',
    },
    {
      code: '+373',
      name: 'Moldova',
      short: 'MD',
    },
    {
      code: '+377',
      name: 'Monaco',
      short: 'MC',
    },
    {
      code: '+976',
      name: 'Mongolia',
      short: 'MN',
    },
    {
      code: '+382',
      name: 'Montenegro',
      short: 'ME',
    },
    {
      code: '+1664',
      name: 'Montserrat',
      short: 'MS',
    },
    {
      code: '+212',
      name: 'Morocco',
      short: 'MA',
    },
    {
      code: '+258',
      name: 'Mozambique',
      short: 'MZ',
    },
    {
      code: '+95',
      name: 'Myanmar',
      short: 'MM',
    },
    {
      code: '+264',
      name: 'Namibia',
      short: 'NA',
    },
    {
      code: '+674',
      name: 'Nauru',
      short: 'NR',
    },
    {
      code: '+977',
      name: 'Nepal',
      short: 'NP',
    },
    {
      code: '+31',
      name: 'Netherlands',
      short: 'NL',
    },
    {
      code: '+599',
      name: 'Netherlands Antilles',
      short: 'AN',
    },
    {
      code: '+687',
      name: 'New Caledonia',
      short: 'NC',
    },
    {
      code: '+64',
      name: 'New Zealand',
      short: 'NZ',
    },
    {
      code: '+505',
      name: 'Nicaragua',
      short: 'NI',
    },
    {
      code: '+227',
      name: 'Niger',
      short: 'NE',
    },
    {
      code: '+234',
      name: 'Nigeria',
      short: 'NG',
    },
    {
      code: '+683',
      name: 'Niue',
      short: 'NU',
    },
    {
      code: '+850',
      name: 'North Korea',
      short: 'KP',
    },
    {
      code: '+47',
      name: 'Norway',
      short: 'NO',
    },
    {
      code: '+968',
      name: 'Oman',
      short: 'OM',
    },
    {
      code: '+92',
      name: 'Pakistan',
      short: 'PK',
    },
    {
      code: '+680',
      name: 'Palau',
      short: 'PW',
    },
    {
      code: '+970',
      name: 'Palestine',
      short: 'PS',
    },
    {
      code: '+507',
      name: 'Panama',
      short: 'PA',
    },
    {
      code: '+675',
      name: 'Papua New Guinea',
      short: 'PG',
    },
    {
      code: '+595',
      name: 'Paraguay',
      short: 'PY',
    },
    {
      code: '+51',
      name: 'Peru',
      short: 'PE',
    },
    {
      code: '+63',
      name: 'Philippines',
      short: 'PH',
    },
    {
      code: '+48',
      name: 'Poland',
      short: 'PL',
    },
    {
      code: '+351',
      name: 'Portugal',
      short: 'PT',
    },
    {
      code: '+1787',
      name: 'Puerto Rico',
      short: 'PR',
    },
    {
      code: '+974',
      name: 'Qatar',
      short: 'QA',
    },
    {
      code: '+262',
      name: 'Reunion',
      short: 'RE',
    },
    {
      code: '+40',
      name: 'Romania',
      short: 'RO',
    },
    {
      code: '+7',
      name: 'Russia',
      short: 'RU',
    },
    {
      code: '+250',
      name: 'Rwanda',
      short: 'RW',
    },
    {
      code: '+590',
      name: 'Saint Barthelemy',
      short: 'BL',
    },
    {
      code: '+290',
      name: 'Saint Helena',
      short: 'SH',
    },
    {
      code: '+1869',
      name: 'Saint Kitts and Nevis',
      short: 'KN',
    },
    {
      code: '+1758',
      name: 'Saint Lucia',
      short: 'LC',
    },
    {
      code: '+590',
      name: 'Saint Martin',
      short: 'MF',
    },
    {
      code: '+508',
      name: 'Saint Pierre and Miquelon',
      short: 'PM',
    },
    {
      code: '+1784',
      name: 'Saint Vincent and the Grenadines',
      short: 'VC',
    },
    {
      code: '+685',
      name: 'Samoa',
      short: 'WS',
    },
    {
      code: '+378',
      name: 'San Marino',
      short: 'SM',
    },
    {
      code: '+239',
      name: 'Sao Tome and Principe',
      short: 'ST',
    },
    {
      code: '+966',
      name: 'Saudi Arabia',
      short: 'SA',
    },
    {
      code: '+221',
      name: 'Senegal',
      short: 'SN',
    },
    {
      code: '+381',
      name: 'Serbia',
      short: 'RS',
    },
    {
      code: '+248',
      name: 'Seychelles',
      short: 'SC',
    },
    {
      code: '+232',
      name: 'Sierra Leone',
      short: 'SL',
    },
    {
      code: '+65',
      name: 'Singapore',
      short: 'SG',
    },
    {
      code: '+721',
      name: 'Sint Maarten',
      short: 'SX',
    },
    {
      code: '+427',
      name: 'Slovakia',
      short: 'SK',
    },
    {
      code: '+386',
      name: 'Slovenia',
      short: 'SI',
    },
    {
      code: '+677',
      name: 'Solomon Islands',
      short: 'SB',
    },
    {
      code: '+252',
      name: 'Somalia',
      short: 'SO',
    },
    {
      code: '+27',
      name: 'South Africa',
      short: 'ZA',
    },
    {
      code: '+82',
      name: 'South Korea',
      short: 'KR',
    },
    {
      code: '+211',
      name: 'South Sudan',
      short: 'SS',
    },
    {
      code: '+34',
      name: 'Spain',
      short: 'ES',
    },
    {
      code: '+94',
      name: 'Sri Lanka',
      short: 'LK',
    },
    {
      code: '+249',
      name: 'Sudan',
      short: 'SD',
    },
    {
      code: '+597',
      name: 'Suriname',
      short: 'SR',
    },
    {
      code: '+268',
      name: 'Swaziland',
      short: 'SZ',
    },
    {
      code: '+46',
      name: 'Sweden',
      short: 'SE',
    },
    {
      code: '+41',
      name: 'Switzerland',
      short: 'CH',
    },
    {
      code: '+963',
      name: 'Syria',
      short: 'SY',
    },
    {
      code: '+886',
      name: 'Taiwan',
      short: 'TW',
    },
    {
      code: '+992',
      name: 'Tajikistan',
      short: 'TJ',
    },
    {
      code: '+255',
      name: 'Tanzania',
      short: 'TZ',
    },
    {
      code: '+66',
      name: 'Thailand',
      short: 'TH',
    },
    {
      code: '+670',
      name: 'Timor Leste',
      short: 'TL',
    },
    {
      code: '+228',
      name: 'Togo',
      short: 'TG',
    },
    {
      code: '+690',
      name: 'Tokelau',
      short: 'TK',
    },
    {
      code: '+676',
      name: 'Tonga',
      short: 'TO',
    },
    {
      code: '+1868',
      name: 'Trinidad and Tobago',
      short: 'TT',
    },
    {
      code: '+216',
      name: 'Tunisia',
      short: 'TN',
    },
    {
      code: '+90',
      name: 'Turkey',
      short: 'TR',
    },
    {
      code: '+993',
      name: 'Turkmenistan',
      short: 'TM',
    },
    {
      code: '+1649',
      name: 'Turks and Caicos Islands',
      short: 'TC',
    },
    {
      code: '+688',
      name: 'Tuvalu',
      short: 'TV',
    },
    {
      code: '+256',
      name: 'Uganda',
      short: 'UG',
    },
    {
      code: '+380',
      name: 'Ukraine',
      short: 'UA',
    },
    {
      code: '+971',
      name: 'United Arab Emirates',
      short: 'AE',
    },
    {
      code: '+44',
      name: 'United Kingdom',
      short: 'GB',
    },
    {
      code: '+1',
      name: 'United States',
      short: 'US',
    },
    {
      code: '+598',
      name: 'Uruguay',
      short: 'UY',
    },
    {
      code: '+998',
      name: 'Uzbekistan',
      short: 'UZ',
    },
    {
      code: '+678',
      name: 'Vanuatu',
      short: 'VU',
    },
    {
      code: '+58',
      name: 'Venezuela',
      short: 'VE',
    },
    {
      code: '+84',
      name: 'Vietnam',
      short: 'VN',
    },
    {
      code: '+681',
      name: 'Wallis and Futuna',
      short: 'WF',
    },
    {
      code: '+967',
      name: 'Yemen',
      short: 'YE',
    },
    {
      code: '+260',
      name: 'Zambia',
      short: 'ZM',
    },
    {
      code: '+263',
      name: 'Zimbabwe',
      short: 'ZW',
    },
  ]);

  const onChange = ({ c, v }: { c: string; v: string }) => {
    setCode(c);
    setVal(v);
    props.onChange(c + v);
  };

  useEffect(() => {
    setCodes(
      listRef.current.map((code: { code: string; name: string; short: string }) => ({
        value: code.code,
        label: code.name + ' (' + code.code + ')',
        html: (
          <Fragment>
            <ReactCountryFlag svg countryCode={code.short} className="mr-5" />
            {code.name + ' (' + code.code + ')'}
          </Fragment>
        ),
      }))
    );

    const actualCode = listRef.current.find((c: any) => props.value?.startsWith(c.code));
    if (actualCode) {
      setCode(actualCode.code);
      setVal(props.value.replace(actualCode.code, ''));
    }
  }, []); // eslint-disable-line

  return (
    <div className={props.className ? props.className : 'col-md-8 col-12'}>
      <div className="row row-phone">
        <div className="col-md-6 col-12">
          <div className="mb-5">{t('passenger.phone_country')}</div>
          <div className="mb-15">
            <SelectCustom
              options={codes}
              value={code}
              onChange={(c) => onChange({ c, v: val })}
              tabIndex={-1}
            />
          </div>
        </div>
        <FormGroup
          className="col-md-6 col-12"
          value={val}
          label={t('passenger.phone')}
          onChange={(v) => onChange({ c: code, v })}
          isSubmitted={props.isSubmitted}
          validation={props.validation}
          allowOnlyNumbers
          maxLength={10}
          name="phone"
        />
      </div>
    </div>
  );
};

export default PhoneInput;
